import type { LoaderFunctionArgs, MetaFunction } from "react-router";
import { Link, useLoaderData, data } from "react-router";
import invariant from "tiny-invariant";
import { companiesInState } from "~/data/.server/business";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/pagination";
import stateConverter from "us-state-converter";
import { capitalizeName } from "~/lib/utils";

export const meta: MetaFunction = ({ params }) => {
  const state = params.state;
  invariant(state, "No state provided");
  return [
    { title: `Businesses in ${state} | MovingData.org` },
    {
      name: "description",
      content: `Discover comprehensive public information on moving companies in ${state} at MovingData.org. Our platform provides valuable insights to help you make informed decisions for your next move.`,
    },
  ];
};

const perPage = 15;
export const loader = async ({ params, request, context }: LoaderFunctionArgs) => {
  const db = context.cloudflare.env.DB;
  const state = params.state;
  const url = new URL(request.url);
  const pageStr = url.searchParams.get("page");
  let page: number = 1;
  try {
    page = parseInt(pageStr ?? "1");
  } catch (e) {}

  invariant(state, "No state provided");
  const companies = await companiesInState(
    db,
    state.toUpperCase() as any,
    perPage,
    (page - 1) * perPage,
  );
  return data(
    { companies, state, currentPage: page },
    { headers: { "Cache-Control": "public, s-maxage=60" } },
  );
};

const constructPageUrl = (state: string, page: number) =>
  `/state/${state}?page=${page}`;

export default function AllCompaniesPage() {
  const { companies, state, currentPage } = useLoaderData<typeof loader>();
  const total = companies?.[0]?.total as number | null;
  const pages = total ? Math.ceil(total / perPage) : 1;
  let pagesToShow = [currentPage - 1, currentPage, currentPage + 1];

  if ((pages ?? 0) - currentPage <= 2) {
    pagesToShow = [pages - 2, pages - 1, pages];
  }
  if (currentPage <= 3) {
    pagesToShow = [1, 2, 3];
  }
  pagesToShow = pagesToShow.filter((p) => p > 0 && p <= pages);

  return (
    <div className="prose mx-auto max-w-screen-lg px-2">
      <h1>All Companies in {stateConverter.fullName(state)}</h1>
      <div className="my-4 rounded-sm bg-white p-4 shadow">
        <Table className="not-prose">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">DOT #</TableHead>
              <TableHead>Company</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {companies.map((company) => (
              <TableRow key={company.dot}>
                <TableCell className="prose font-medium">
                  {company.dot}
                </TableCell>
                <TableCell className="prose font-medium underline hover:text-primary">
                  <Link
                    to={`/state/${state.toLowerCase()}/business/${company.slug}`}
                  >
                    {capitalizeName((company.name as string) ?? "")}
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            {pages > 1 && (
              <tr>
                <td className="not-prose py-4" colSpan={3}>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          to={constructPageUrl(
                            state,
                            currentPage - 1 < 1 ? 1 : currentPage - 1,
                          )}
                        />
                      </PaginationItem>
                      {currentPage > 3 && (
                        <>
                          <PaginationItem>
                            <PaginationLink to={constructPageUrl(state, 1)}>
                              1
                            </PaginationLink>
                          </PaginationItem>
                          <PaginationItem>
                            <PaginationEllipsis />
                          </PaginationItem>
                        </>
                      )}

                      {pagesToShow.map((p) => (
                        <PaginationItem key={p}>
                          <PaginationLink
                            to={constructPageUrl(state, p)}
                            isActive={p === currentPage}
                          >
                            {p}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      {(pages ?? 0) - currentPage > 2 && (
                        <>
                          <PaginationItem>
                            <PaginationEllipsis />
                          </PaginationItem>
                          <PaginationItem>
                            <PaginationLink to={constructPageUrl(state, pages)}>
                              {pages}
                            </PaginationLink>
                          </PaginationItem>
                        </>
                      )}
                      <PaginationItem>
                        <PaginationNext
                          to={constructPageUrl(
                            state,
                            currentPage + 1 >= pages ? pages : currentPage + 1,
                          )}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </td>
              </tr>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
